/**
 * ReportButton - one click to tell somebody something is wrong
 * ------------------------------------------------------------
 * Deliberately present on profiles and chore pages, not hidden in the terms.
 * If a student feels unsafe, finding the right email address is one obstacle
 * too many.
 *
 * Blocking sits alongside reporting because they do different jobs: a report
 * asks somebody else to act, a block takes effect immediately and needs no
 * explanation.
 */

import { useState } from 'react';
import { Flag, ShieldOff, AlertTriangle } from 'lucide-react';

import { safetyApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';
import { useI18n } from '../hooks/useI18n';

const REASONS = [
  { value: 'made_me_uncomfortable', label: 'report.r1' },
  { value: 'inappropriate_behaviour', label: 'report.r2' },
  { value: 'unsafe_work', label: 'report.r3' },
  { value: 'did_not_pay', label: 'report.r4' },
  { value: 'did_not_turn_up', label: 'report.r5' },
  { value: 'fake_profile', label: 'report.r6' },
  { value: 'abusive_review', label: 'report.r7' },
  { value: 'something_else', label: 'report.r8' },
];

export default function ReportButton({ aboutUser, aboutChore, personName }) {
  const { t } = useI18n();
  // Not a default in the signature: that cannot call t(), so an unnamed
  // person came out as English "this person" on an Italian screen.
  const name = personName || t('report.thisPerson');
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  // Only signed-in people can report, and never themselves.
  if (!user) return null;
  if (aboutUser && String(aboutUser) === String(user._id)) return null;

  const submit = async () => {
    if (!reason) {
      setError(t('report.choosePlease'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await safetyApi.report({ about: aboutUser, chore: aboutChore, reason, details });
      setDone(res.message);
      setReason('');
      setDetails('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const block = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await safetyApi.block(aboutUser);
      setDone(res.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* The words are wrapped so a cramped header (the message screen, on a
          phone) can drop to icons alone without losing the button. aria-label
          and title carry the meaning when the text is not drawn - the icons
          themselves are aria-hidden, so without these the buttons would have
          no name at all for a screen reader. */}
      <div className="row report-actions" style={{ gap: 'var(--space-xs)' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-inline"
          onClick={() => setOpen(true)}
          aria-label={t('report.reportName', { name })}
          title={t('report.reportName', { name })}
        >
          <Flag size={16} /> <span className="btn-label">{t('report.button')}</span>
        </button>
        {aboutUser && (
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-inline"
            onClick={() => setBlockOpen(true)}
            aria-label={t('report.blockName', { name })}
            title={t('report.blockName', { name })}
          >
            <ShieldOff size={16} /> <span className="btn-label">{t('report.blockButton')}</span>
          </button>
        )}
      </div>

      {/* ---------------- REPORT ---------------- */}
      <Modal
        open={open}
        onClose={() => { setOpen(false); setDone(''); setError(''); }}
        title={done ? t('report.thankYou') : t('report.reportName', { name })}
        actions={
          done ? (
            <button type="button" className="btn btn-primary btn-inline" onClick={() => { setOpen(false); setDone(''); }}>
              {t('report.close')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-ghost btn-inline" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger btn-inline" onClick={submit} disabled={busy}>
                {busy ? t('report.sending') : t('report.send')}
              </button>
            </>
          )
        }
      >
        {done ? (
          <div className="alert alert-success">{done}</div>
        ) : (
          <>
            <div className="alert alert-warning" style={{ marginBottom: 'var(--space-sm)' }}>
              <AlertTriangle size={18} aria-hidden="true" />
              <div className="text-sm">
                <strong>{t('report.emergency')}</strong> {t('report.emergencyBody')}
              </div>
            </div>

            <div className="field">
              <label htmlFor="report-reason">{t('report.whatHappened')} *</label>
              <select id="report-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="">{t('report.chooseOne')}</option>
                {REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {t(r.label)}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="report-details">{t('report.anythingToAdd')}</label>
              <textarea
                id="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={2000}
                placeholder={t('report.detailsPlaceholder')}
              />
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <p className="text-sm text-muted">
              {t('report.realPerson')}
            </p>
          </>
        )}
      </Modal>

      {/* ---------------- BLOCK ---------------- */}
      <Modal
        open={blockOpen}
        onClose={() => { setBlockOpen(false); setDone(''); setError(''); }}
        title={done ? t('report.blocked') : t('report.blockNameQ', { name })}
        actions={
          done ? (
            <button type="button" className="btn btn-primary btn-inline" onClick={() => { setBlockOpen(false); setDone(''); window.location.reload(); }}>
              {t('report.close')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-ghost btn-inline" onClick={() => setBlockOpen(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger btn-inline" onClick={block} disabled={busy}>
                {busy ? t('report.blocking') : t('report.blockThem')}
              </button>
            </>
          )
        }
      >
        {done ? (
          <div className="alert alert-success">{done}</div>
        ) : (
          <>
            <p>
              {t('report.blockBody', { name })}
            </p>
            <p className="text-muted text-sm" style={{ marginTop: 'var(--space-sm)' }}>
              {t('report.blockUndo')}
            </p>
          </>
        )}
      </Modal>
    </>
  );
}
